import React, { useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { Table } from './Table'
import { SquareAmountCell } from './SquareAmountCell'
import { Button } from '../../../components/shadcn/Button'
import { ReturnFormData } from './EditReturnItemModal'
import { faTrash } from '@fortawesome/pro-solid-svg-icons'
import { ActiveReturnsTable_DepotFragmentFragment } from '../../../gql/graphql'
import { ProductCell, ProductCell_ProductFragment } from './ProductCell'
import { useBasicEntities } from '../../../hooks/useBasicEntities'

export const ActiveReturnsTable_DepotFragment = graphql(`
  fragment ActiveReturnsTable_DepotFragment on Depot {
    id
    name
  }
`)

interface ActiveReturnTableItem extends ReturnFormData {
  productName: string
  depotName: string
  index: number
  product?: FragmentType<typeof ProductCell_ProductFragment>
}

interface ActiveReturnsTableProps {
  activeReturns: (ReturnFormData & { product?: FragmentType<typeof ProductCell_ProductFragment> })[]
  productNames: Record<number, string>
  depots: ActiveReturnsTable_DepotFragmentFragment[]
  onRemove: (index: number) => void
}

const columnHelper = createColumnHelper<ActiveReturnTableItem>()

export const ActiveReturnsTable: React.FC<ActiveReturnsTableProps> = ({ activeReturns, productNames, onRemove }) => {
  'use no memo'
  const { depots } = useBasicEntities()

  const tableData = useMemo<ActiveReturnTableItem[]>(
    () =>
      activeReturns.map((item, index) => {
        const depot = depots.find(d => d.id.toString() === item.depotId.toString())

        return {
          ...item,
          productName: productNames[item.productId] || 'Neznámý produkt',
          depotName: depot?.name || 'Neznámý sklad',
          index,
        }
      }),
    [activeReturns, productNames, depots],
  )

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'product',
        header: 'Název zboží',
        cell: ({ row }) => <ProductCell product={row.original.product} />,
        // @ts-ignore
        size: '2fr',
      }),
      columnHelper.accessor('depotName', {
        header: 'Sklad',
        size: 200,
      }),
      columnHelper.accessor('expirationDate', {
        header: 'Expirace',
        cell: ({ getValue }) => getValue() || '-',
        size: 120,
      }),
      columnHelper.accessor('batch', {
        header: 'Šarže',
        cell: ({ getValue }) => getValue() || '-',
        size: 120,
      }),
      columnHelper.display({
        id: 'position',
        header: 'Pozice',
        cell: ({ row }) => {
          const { position1, position2, position3 } = row.original
          if (!position1 && !position2 && !position3) return '-'
          return `${position1 || ''}-${position2 || ''}-${position3 || ''}`
        },
        size: 150,
      }),
      columnHelper.accessor('amount', {
        header: () => <div className="text-center">Počet ks</div>,
        cell: ({ getValue }) => <SquareAmountCell>{getValue()}</SquareAmountCell>,
        size: 100,
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Button
              variant="destructive"
              size="icon"
              icon={faTrash}
              title="Odebrat"
              onClick={() => onRemove(row.original.index)}
            />
          </div>
        ),
        size: 80,
      }),
    ],
    [onRemove],
  )

  const columnVisibility = useMemo(
    () => ({
      batch: !tableData.every(item => !item.batch),
      expirationDate: !tableData.every(item => !item.expirationDate),
      position: !tableData.every(item => !item.position1 && !item.position2 && !item.position3),
    }),
    [tableData],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
  })

  return <Table table={table} />
}
